import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";
import { StatusBadge } from "@/components/StatusBadge";
import { BottomNav } from "@/components/BottomNav";
import { Heart, ChevronLeft, Share2, MapPin, Instagram, Facebook, Globe, Award } from "lucide-react";
import { BidNowButton } from "@/components/BidNowButton";
import { SeeVideoButton } from "@/components/SeeVideoButton";
import { toast } from "sonner";
import { SitePopup } from "@/components/SitePopup";

const DIV_KEY = { "Adult Undersaddle": "adult", "Adult In-Hand": "inhand", "Youth In-Hand": "youth" };

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-border bg-card p-3 text-center">
    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-display text-lg font-bold text-primary">{value || "—"}</p>
  </div>
);

export default function MustangProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [error, setError] = useState(false);
  const [result, setResult] = useState(null);
  const [favConfig, setFavConfig] = useState(null);
  const [active, setActive] = useState(0);
  const { isFav, toggle } = useFavorites();

  useEffect(() => {
    api.get(`/horses/${id}`).then((r) => setHorse(r.data)).catch(() => setError(true));
    api.get(`/results/${id}`).then((r) => setResult(r.data)).catch(() => {});
    api.get(`/fanfav/config`).then((r) => setFavConfig(r.data)).catch(() => {});
  }, [id]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: horse.horse_name, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
    } catch { /* dismissed */ }
  };

  if (error) return (
    <div className="grid min-h-screen place-items-center bg-background p-8 text-center">
      <div><p className="font-display text-2xl font-bold text-primary">Mustang not found</p>
        <Link to="/mustangs" className="mt-3 inline-block text-secondary underline">Back to directory</Link></div>
    </div>
  );
  if (!horse) return <div className="min-h-screen bg-background" />;

  const fav = isFav(horse.id);
  const photos = (horse.current_photos?.length ? horse.current_photos : [horse.intake_photo]).filter(Boolean);
  const photo = photos[Math.min(active, photos.length - 1)] || null;

  return (
    <div className="min-h-screen bg-background">
      <SitePopup />
      <main className="mx-auto max-w-lg pb-24">
        {/* Hero */}
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
          {photo ? (
            <img src={photo} alt={horse.horse_name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/15 to-secondary/15">
              <span className="font-display text-6xl font-bold text-primary/25">HIP #{horse.hip_number}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button data-testid="back-btn" onClick={() => navigate(-1)}
            className="absolute left-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur">
            <ChevronLeft size={20} />
          </button>
          <div className="absolute right-3 top-3 flex gap-2">
            <button data-testid="share-btn" onClick={share}
              className="grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur"><Share2 size={18} /></button>
            <button data-testid="profile-fav-btn" onClick={() => toggle(horse.id)}
              className="grid h-10 w-10 place-items-center rounded-full bg-white/90 backdrop-blur">
              <Heart size={18} fill={fav ? "hsl(26 50% 51%)" : "none"} stroke={fav ? "hsl(26 50% 51%)" : "currentColor"} />
            </button>
          </div>
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-bold">HIP #{horse.hip_number}</span>
            <h1 className="mt-1 font-display text-3xl font-extrabold">{horse.horse_name}</h1>
            <p className="text-sm text-white/85">Trained by {horse.trainer_name}</p>
          </div>
        </div>

        {photos.length > 1 && (
          <div className="flex gap-2 overflow-x-auto px-4 pt-3 no-scrollbar" data-testid="profile-gallery">
            {photos.map((p, i) => (
              <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setActive(i)}
                className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-colors ${i === active ? "border-secondary" : "border-transparent opacity-70"}`}>
                <img src={p} alt={`${horse.horse_name} photo ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {horse.archived_year != null ? <ArchivedDetails horse={horse} /> : (
        <div className="px-4 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">{horse.division}</span>
            <StatusBadge status={horse.auction_status} />
            {(horse.is_scratched || horse.is_withdrawn) && <StatusBadge status="Canceled" />}
            {horse.placing && <span className="rounded-full bg-secondary/15 px-3 py-1 text-xs font-bold text-secondary">Placing #{horse.placing}</span>}
          </div>

          {(() => {
            const showBid = horse.auction_status === "In Auction" && horse.auction_url && !(horse.is_scratched || horse.is_withdrawn);
            const showVideo = !!horse.video_url;
            if (!showBid && !showVideo) return null;
            return (
              <div className="mt-4 flex gap-2">
                {showBid && <BidNowButton url={horse.auction_url} className="flex-1" />}
                {showVideo && <SeeVideoButton url={horse.video_url} className="flex-1" />}
              </div>
            );
          })()}

          <div className="mt-4 grid grid-cols-3 gap-2">
            <Stat label="Age" value={horse.horse_age ? `${horse.horse_age} yrs` : ""} />
            <Stat label="Sex" value={horse.sex} />
            <Stat label="Height" value={horse.height} />
            <Stat label="Color" value={horse.color} />
            <Stat label="State" value={horse.trainer_state} />
            <Stat label="Placing" value={horse.placing ? `#${horse.placing}` : ""} />
          </div>

          <div className="mt-4 rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">HMA (Herd Management Area)</p>
            <p className="mt-0.5 flex items-center gap-1.5 font-semibold text-primary"><MapPin size={15} className="text-secondary" />{horse.hma || "—"}</p>
          </div>

          <Section title="Trainer Biography" text={horse.trainer_bio} />
          <Section title="120-Day Training Story" text={horse.training_story} />

          {/* Published scores */}
          <div className="mt-4 rounded-xl border border-border bg-card p-4">
            <h2 className="font-display text-lg font-bold text-primary">Published Results</h2>
            {result ? (
              <>
                <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
                  <ScoreCell label="H&C" value={result.classes?.hc?.total} />
                  <ScoreCell label="Trail" value={result.classes?.trail?.total} />
                  <ScoreCell label="Freestyle" value={result.classes?.freestyle?.total} />
                  <ScoreCell label="+B / -P" value={`${result.total_bonus || 0}/${result.total_penalties || 0}`} />
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={result.status === "final" ? "Final" : "Published"} />
                    {result.placing && <span className="text-sm font-bold text-secondary">Placing #{result.placing}{result.tied ? " (tied)" : ""}</span>}
                  </div>
                  <span className="font-display text-xl font-bold text-primary">Overall: {result.overall_total ?? "—"}</span>
                </div>
              </>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">No published results are available for this horse yet.</p>
            )}
          </div>

          {/* Fan Favorite vote */}
          {favConfig?.[`${DIV_KEY[horse.division]}_open`] && horse.fav_eligible !== false && !(horse.is_scratched || horse.is_withdrawn || horse.is_disqualified) && (
            <Link to="/fan-favorite" data-testid="profile-vote-link"
              className="mt-4 flex h-12 items-center justify-center gap-2 rounded-full bg-secondary font-bold text-white active:scale-95">
              <Award size={18} /> Cast Your Fan Favorite Vote
            </Link>
          )}

          {horse.auction_status && (
            <div className="mt-4 rounded-xl border border-secondary/40 bg-secondary/5 p-4">
              <h2 className="font-display text-lg font-bold text-primary">Auction</h2>
              <p className="mt-1 text-sm"><StatusBadge status={horse.auction_status} /></p>
              {horse.sale_info && <p className="mt-2 text-sm text-foreground/80">{horse.sale_info}</p>}
              {horse.sale_price != null && <p className="mt-1 font-display text-xl font-bold text-primary">Sold: ${horse.sale_price}</p>}
              <SubSection title="Auction Terms" text={horse.auction_terms} />
              <SubSection title="Buyer Requirements" text={horse.buyer_requirements} />
              <SubSection title="Payment Info" text={horse.payment_info} />
              <SubSection title="Pickup Info" text={horse.pickup_info} />
            </div>
          )}

          {Object.keys(horse.social_links || {}).length > 0 && (
            <div className="mt-4 flex gap-3">
              {horse.social_links.instagram && <a href={horse.social_links.instagram} target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center rounded-full border border-border"><Instagram size={18} /></a>}
              {horse.social_links.facebook && <a href={horse.social_links.facebook} target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center rounded-full border border-border"><Facebook size={18} /></a>}
              {horse.social_links.website && <a href={horse.social_links.website} target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center rounded-full border border-border"><Globe size={18} /></a>}
            </div>
          )}
        </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

const ArchivedDetails = ({ horse }) => {
  const s = horse.archive_snapshot || {};
  return (
    <div className="px-4 pt-4" data-testid="archived-profile">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground">{horse.division}</span>
        <Link to={`/mustangs?year=${horse.archived_year}`} data-testid="archived-year-badge" className="rounded-full border border-secondary/50 px-3 py-1 text-xs font-bold text-secondary">{horse.archived_year} Makeover</Link>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="archived-placing">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Final Placing</p>
          <p className="mt-1 font-display text-2xl font-bold text-primary">{s.placing ? `#${s.placing}${s.tied ? " (tie)" : ""}` : "—"}</p>
          {s.overall_total != null && <p className="text-xs text-muted-foreground">Overall score {s.overall_total}</p>}
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="archived-sale-price">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Sale Price</p>
          <p className="mt-1 font-display text-2xl font-bold text-primary">{horse.sale_price != null ? `$${Number(horse.sale_price).toLocaleString()}` : "—"}</p>
        </div>
      </div>
    </div>
  );
};

const SubSection = ({ title, text }) =>
  text ? (
    <div className="mt-3 border-t border-secondary/20 pt-3">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</p>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground/80">{text}</p>
    </div>
  ) : null;

const Section = ({ title, text }) =>
  text ? (
    <div className="mt-4">
      <h2 className="font-display text-lg font-bold text-primary">{title}</h2>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground/80">{text}</p>
    </div>
  ) : null;

const ScoreCell = ({ label, value }) => (
  <div className="rounded-lg bg-muted py-1.5">
    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-semibold text-primary">{value ?? "—"}</p>
  </div>
);
